// src/redux/classSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  get_session,
  add_session,
  update_session,
  delete_session,
  get_session_list,
} from "../Service/ApiServices.js";
import { toast } from "react-toastify";

const initialState = {
  sessions: [],
  sessionlist: [],
  school_id: "",
  totalPages: 0,
  pageSize: 10,
  count: 0,
  activePage: 1,
  loading: false,
  error: null,
  addLoading: false,
  IsEditing: false,
  formData: {
    start_date: "",
    end_date: "",
    name: "",
  },
};

// Async Thunks for API Calls
export const fetchSessionList = createAsyncThunk(
  "session/fetchSessionlist",
  async ({ school_id, token, signal }, { rejectWithValue }) => {
    try {
      const response = await get_session_list(school_id, token, signal);
      if (response.status) {
        return response.response;
      } else {
        return rejectWithValue(response.response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async Thunks for API Calls
export const fetchSession = createAsyncThunk(
  "session/fetchSession",
  async (
    { page, page_size, school_id, token, signal },
    { rejectWithValue }
  ) => {
    try {
      const response = await get_session(
        page,
        page_size,
        school_id,
        token,
        signal
      );
      if (response.status) {
        return response.response;
      } else {
        return rejectWithValue(response.response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
export const createSession = createAsyncThunk(
  "session/createSession",
  async ({ data, school_id, token, signal }, { rejectWithValue }) => {
    try {
      const response = await add_session(data, school_id, token, signal);
      if (response.status) {
        return response.response;
      } else {
        return rejectWithValue(response.response);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
export const updateSession = createAsyncThunk(
  "session/updateSession",
  async ({ data, token, signal }, { rejectWithValue }) => {
    try {
      const response = await update_session(data, token, signal);
      if (response.status) {
        return response.response;
      } else {
        return rejectWithValue(response.response);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSession = createAsyncThunk(
  "session/deleteSession",
  async ({ ids, token, signal }, { rejectWithValue }) => {
    try {
      const response = await delete_session({ ids }, token, signal);
      if (response.status) {
        return response.response;
      } else {
        return rejectWithValue(response.response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
// Class Slice
const sessionSlice = createSlice({
  name: "sessions",
  initialState,
  reducers: {
    setActivePage(state, action) {
      state.activePage = action.payload;
    },
    updateFormData(state, action) {
      const { name, value } = action.payload;
      state.formData[name] = value;
    },
    EditFormData(state, action) {
      // The action should contain all the fields that need to be updated
      const { id, name, start_date, end_date } = action.payload;
      state.formData = { id, name, start_date, end_date };
    },
    resetFormData(state) {
      state.formData = {
        start_date: "",
        end_date: "",
        name: "",
      };
    },
    setSession(state, action) {
      state.sessions = action.payload;
    },
    setIsEditing(state, action) {
      state.IsEditing = action.payload;
    },
    setPageSize(state, action) {
      state.pageSize = action.payload;
    },
    setSchoolId(state, action) {
      state.school_id = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSessionList.fulfilled, (state, action) => {
        state.sessionlist = action.payload.data;
      })
      // Fetch Classes
      .addCase(fetchSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSession.fulfilled, (state, action) => {
        state.loading = false;
        state.sessions = action.payload.results;
        state.count = action.payload.count;
        state.totalPages = Math.ceil(action.payload.count / state.pageSize);
      })
      .addCase(fetchSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add Class
      .addCase(createSession.pending, (state) => {
        state.addLoading = true;
      })
      .addCase(createSession.fulfilled, (state, action) => {
        state.addLoading = false;
        state.IsEditing = false;
        toast.success(action.payload.message || "Something went wrong!");
      })
      .addCase(createSession.rejected, (state, action) => {
        state.addLoading = false;
        state.error = action?.payload?.data?.message;
        const errorMessage =
          action?.payload?.data?.message || "Something went wrong!";
        toast.info(errorMessage);
        state.IsEditing = false;
      })
      // Update Class
      .addCase(updateSession.pending, (state) => {
        state.addLoading = true;
      })
      .addCase(updateSession.fulfilled, (state, action) => {
        state.addLoading = false;
        state.IsEditing = false;

        toast.success(action?.payload?.message || "Something went wrong!");
      })
      .addCase(updateSession.rejected, (state, action) => {
        state.addLoading = false;
               state.error = action?.payload?.data?.message;
               state.IsEditing = false;
               const errorMessage = action?.payload?.data?.message || "Something went wrong!";
               toast.info(errorMessage); 
      })
      // Delete Class
      .addCase(deleteSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSession.fulfilled, (state, action) => {
        state.loading = false;
        toast.success(action?.payload?.message || "Something went wrong!");
      })
      .addCase(deleteSession.rejected, (state, action) => {
        state.loading = false;
        toast.info(action?.payload?.message || "Something went wrong!");
        state.error = action.payload;
      });
  },
});

// Export the actions and the reducer
export const {
  setSession,
  setActivePage,
  updateFormData,
  EditFormData,
  resetFormData,
  setIsEditing,
  setSchoolId,
  setPageSize,
} = sessionSlice.actions;
export default sessionSlice.reducer;
